import type { ExploreGenerationPreferences } from '@/components/explore/exploreGenerationSettingsStore';
import type { Creativity } from '@/types/custom-style';
import {
  CONVERSATION_CAPABILITY_CONTRACTS,
  type ConversationCapabilityKey,
  type ConversationCapabilitySlotKey,
} from '@/features/conversation-capabilities/contract';

export type PendingExploreDraftMode = 'image' | 'video';

export interface PendingExploreDraftStyle {
  id: string;
  presetId: string;
  name: string;
  nameEn: string;
  nameZh: string;
  coverUrl: string;
  creativity?: Creativity;
}

export interface PendingExploreDraftAttachment {
  id: string;
  name: string;
  type: string;
  lastModified: number;
  capabilitySlotKey?: ConversationCapabilitySlotKey;
}

export interface PendingExploreDraft {
  version: 2;
  entry?: 'consultation';
  generationPreferences?: ExploreGenerationPreferences;
  mode: PendingExploreDraftMode;
  prompt: string;
  capability?: ConversationCapabilityKey;
  style?: PendingExploreDraftStyle;
  attachments: PendingExploreDraftAttachment[];
  createdAt: number;
}

export interface PendingExploreComposerResumeDetail {
  generationPreferences?: ExploreGenerationPreferences;
  capability?: ConversationCapabilityKey;
}

interface StoredPendingExploreAttachment extends PendingExploreDraftAttachment {
  file: Blob;
  createdAt: number;
}

const PENDING_EXPLORE_DRAFT_STORAGE_KEY = 'dobidobi:pending-explore-draft:v1';
const PENDING_EXPLORE_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const PENDING_EXPLORE_ATTACHMENT_DATABASE = 'dobidobi-pending-explore';
const PENDING_EXPLORE_ATTACHMENT_STORE = 'attachments';
const PENDING_EXPLORE_ATTACHMENT_DATABASE_VERSION = 1;
export const PENDING_EXPLORE_COMPOSER_RESUME_EVENT = 'dobidobi:resume-pending-explore-composer';
const PENDING_EXPLORE_CAPABILITY_SLOT_KEYS = new Set<ConversationCapabilitySlotKey>([
  ...Object.values(CONVERSATION_CAPABILITY_CONTRACTS).flatMap((contract) => (
    contract.slots.map((slot) => slot.key)
  )),
  'subject',
]);

function isDraftMode(value: unknown): value is PendingExploreDraftMode {
  return value === 'image' || value === 'video';
}

function isPendingExploreCapability(value: unknown): value is ConversationCapabilityKey {
  return (
    typeof value === 'string'
    && Object.prototype.hasOwnProperty.call(CONVERSATION_CAPABILITY_CONTRACTS, value)
  );
}

function isPendingExploreDraftStyle(value: unknown): value is PendingExploreDraftStyle {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<PendingExploreDraftStyle>;
  return (
    typeof candidate.id === 'string'
    && candidate.id.trim().length > 0
    && typeof candidate.presetId === 'string'
    && candidate.presetId.trim().length > 0
    && typeof candidate.name === 'string'
    && candidate.name.trim().length > 0
    && typeof candidate.nameEn === 'string'
    && candidate.nameEn.trim().length > 0
    && typeof candidate.nameZh === 'string'
    && candidate.nameZh.trim().length > 0
    && typeof candidate.coverUrl === 'string'
    && candidate.coverUrl.trim().length > 0
    && (candidate.creativity === undefined || [0, 25, 50, 75, 100].includes(candidate.creativity))
  );
}

function isPendingExploreDraftAttachment(value: unknown): value is PendingExploreDraftAttachment {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<PendingExploreDraftAttachment>;
  return (
    typeof candidate.id === 'string'
    && candidate.id.trim().length > 0
    && typeof candidate.name === 'string'
    && typeof candidate.type === 'string'
    && typeof candidate.lastModified === 'number'
    && Number.isFinite(candidate.lastModified)
    && (
      candidate.capabilitySlotKey === undefined
      || PENDING_EXPLORE_CAPABILITY_SLOT_KEYS.has(candidate.capabilitySlotKey)
    )
  );
}

function normalizePendingExploreDraft(value: unknown): PendingExploreDraft | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as {
    version?: unknown;
    entry?: unknown;
    generationPreferences?: unknown;
    mode?: unknown;
    prompt?: unknown;
    capability?: unknown;
    style?: unknown;
    attachments?: unknown;
    createdAt?: unknown;
  };
  if (
    !isDraftMode(candidate.mode)
    || typeof candidate.prompt !== 'string'
    || typeof candidate.createdAt !== 'number'
    || !Number.isFinite(candidate.createdAt)
  ) {
    return null;
  }

  if (candidate.version === 1) {
    if (!candidate.prompt.trim()) {
      return null;
    }
    return {
      version: 2,
      mode: candidate.mode,
      prompt: candidate.prompt.trim(),
      attachments: [],
      createdAt: candidate.createdAt,
    };
  }

  if (candidate.version !== 2) {
    return null;
  }

  const attachments = Array.isArray(candidate.attachments)
    ? candidate.attachments.filter(isPendingExploreDraftAttachment)
    : [];
  const style = candidate.style && isPendingExploreDraftStyle(candidate.style)
    ? candidate.style
    : undefined;
  const capability = isPendingExploreCapability(candidate.capability)
    ? candidate.capability
    : undefined;
  const prompt = candidate.prompt.trim();
  if (!prompt && attachments.length === 0) {
    return null;
  }

  return {
    version: 2,
    mode: candidate.mode,
    entry: candidate.entry === 'consultation' ? 'consultation' : undefined,
    generationPreferences: candidate.generationPreferences && typeof candidate.generationPreferences === 'object'
      ? candidate.generationPreferences as ExploreGenerationPreferences : undefined,
    prompt,
    capability,
    style,
    attachments,
    createdAt: candidate.createdAt,
  };
}

function openPendingExploreAttachmentDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(
      PENDING_EXPLORE_ATTACHMENT_DATABASE,
      PENDING_EXPLORE_ATTACHMENT_DATABASE_VERSION,
    );

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(PENDING_EXPLORE_ATTACHMENT_STORE)) {
        database.createObjectStore(PENDING_EXPLORE_ATTACHMENT_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('pending_explore_database_open_failed'));
  });
}

async function putPendingExploreAttachment(record: StoredPendingExploreAttachment): Promise<void> {
  const database = await openPendingExploreAttachmentDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(PENDING_EXPLORE_ATTACHMENT_STORE, 'readwrite');
      transaction.objectStore(PENDING_EXPLORE_ATTACHMENT_STORE).put(record);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('pending_explore_attachment_write_failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('pending_explore_attachment_write_aborted'));
    });
  } finally {
    database.close();
  }
}

async function readPendingExploreAttachment(id: string): Promise<StoredPendingExploreAttachment | null> {
  const database = await openPendingExploreAttachmentDatabase();
  try {
    return await new Promise<StoredPendingExploreAttachment | null>((resolve, reject) => {
      const transaction = database.transaction(PENDING_EXPLORE_ATTACHMENT_STORE, 'readonly');
      const request = transaction.objectStore(PENDING_EXPLORE_ATTACHMENT_STORE).get(id);
      request.onsuccess = () => resolve((request.result as StoredPendingExploreAttachment | undefined) ?? null);
      request.onerror = () => reject(request.error ?? new Error('pending_explore_attachment_read_failed'));
    });
  } finally {
    database.close();
  }
}

async function deletePendingExploreAttachments(ids: string[]): Promise<void> {
  if (typeof window === 'undefined' || !window.indexedDB || ids.length === 0) {
    return;
  }

  const database = await openPendingExploreAttachmentDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(PENDING_EXPLORE_ATTACHMENT_STORE, 'readwrite');
      const store = transaction.objectStore(PENDING_EXPLORE_ATTACHMENT_STORE);
      ids.forEach((id) => store.delete(id));
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error('pending_explore_attachment_delete_failed'));
      transaction.onabort = () => reject(transaction.error ?? new Error('pending_explore_attachment_delete_aborted'));
    });
  } finally {
    database.close();
  }
}

function readStoredDraftWithoutConsuming(): PendingExploreDraft | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(PENDING_EXPLORE_DRAFT_STORAGE_KEY);
    return raw ? normalizePendingExploreDraft(JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

export function savePendingExploreDraft(
  draft: Pick<PendingExploreDraft, 'mode' | 'prompt'> & Partial<Pick<PendingExploreDraft, 'capability' | 'style' | 'entry' | 'generationPreferences'>>,
): boolean {
  if (typeof window === 'undefined' || !draft.prompt.trim()) {
    return false;
  }

  try {
    const previousDraft = readStoredDraftWithoutConsuming();
    if (previousDraft?.attachments.length) {
      void deletePendingExploreAttachments(previousDraft.attachments.map((attachment) => attachment.id));
    }

    const payload: PendingExploreDraft = {
      version: 2,
      mode: draft.mode,
      entry: draft.entry,
      generationPreferences: draft.generationPreferences,
      prompt: draft.prompt.trim(),
      capability: draft.capability,
      style: draft.style,
      attachments: [],
      createdAt: Date.now(),
    };
    window.sessionStorage.setItem(PENDING_EXPLORE_DRAFT_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export async function savePendingExploreDraftWithFiles({
  mode,
  prompt,
  capability,
  style,
  files,
  capabilitySlotKeys,
  entry,
  generationPreferences,
}: {
  mode: PendingExploreDraftMode;
  prompt: string;
  capability?: ConversationCapabilityKey;
  style?: PendingExploreDraftStyle;
  files: File[];
  entry?: 'consultation';
  generationPreferences?: ExploreGenerationPreferences;
  capabilitySlotKeys?: Array<ConversationCapabilitySlotKey | undefined>;
}): Promise<boolean> {
  if (
    typeof window === 'undefined'
    || !window.indexedDB
    || files.length === 0
    || files.some((file) => file.size <= 0)
  ) {
    return false;
  }

  const createdAt = Date.now();
  const attachments = files.map((file, index) => ({
    id: crypto.randomUUID(),
    name: file.name,
    type: file.type,
    lastModified: file.lastModified,
    capabilitySlotKey: capabilitySlotKeys?.[index],
  }));
  const previousDraft = readStoredDraftWithoutConsuming();

  try {
    await Promise.all(files.map((file, index) => putPendingExploreAttachment({
      ...attachments[index],
      file,
      createdAt,
    })));

    const payload: PendingExploreDraft = {
      version: 2,
      mode,
      entry,
      generationPreferences,
      prompt: prompt.trim(),
      capability,
      style,
      attachments,
      createdAt,
    };
    window.sessionStorage.setItem(PENDING_EXPLORE_DRAFT_STORAGE_KEY, JSON.stringify(payload));

    if (previousDraft?.attachments.length) {
      await deletePendingExploreAttachments(
        previousDraft.attachments.map((attachment) => attachment.id),
      );
    }
    return true;
  } catch {
    await deletePendingExploreAttachments(
      attachments.map((attachment) => attachment.id),
    ).catch(() => undefined);
    return false;
  }
}

export function consumePendingExploreDraft(): PendingExploreDraft | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(PENDING_EXPLORE_DRAFT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = normalizePendingExploreDraft(JSON.parse(raw) as unknown);
    window.sessionStorage.removeItem(PENDING_EXPLORE_DRAFT_STORAGE_KEY);

    if (!parsed) {
      return null;
    }
    if (Date.now() - parsed.createdAt > PENDING_EXPLORE_DRAFT_MAX_AGE_MS) {
      void deletePendingExploreAttachments(parsed.attachments.map((attachment) => attachment.id));
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export async function consumePendingExploreDraftFiles(
  draft: PendingExploreDraft,
): Promise<File[]> {
  if (typeof window === 'undefined' || !window.indexedDB || draft.attachments.length === 0) {
    return [];
  }

  const files: File[] = [];
  try {
    for (const attachment of draft.attachments) {
      const stored = await readPendingExploreAttachment(attachment.id);
      if (!stored?.file) {
        continue;
      }

      files.push(new File(
        [stored.file],
        attachment.name || stored.name || 'reference-image',
        {
          type: attachment.type || stored.type || stored.file.type,
          lastModified: attachment.lastModified || stored.lastModified,
        },
      ));
    }
    return files;
  } finally {
    await deletePendingExploreAttachments(
      draft.attachments.map((attachment) => attachment.id),
    ).catch(() => undefined);
  }
}
